import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Tag, Plus, Pencil, Trash2, GripVertical, Save, X, Loader2,
  Clock, AlertTriangle, DoorOpen, HeartPulse, Shield, Phone, Home, MapPin, Navigation
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Iconos disponibles
const ICONOS_DISPONIBLES = [
  { id: 'clock', nombre: 'Reloj', icon: Clock },
  { id: 'alert-triangle', nombre: 'Alerta', icon: AlertTriangle },
  { id: 'door-open', nombre: 'Puerta', icon: DoorOpen },
  { id: 'heart-pulse', nombre: 'Corazón', icon: HeartPulse },
  { id: 'shield', nombre: 'Escudo', icon: Shield },
  { id: 'phone', nombre: 'Teléfono', icon: Phone },
  { id: 'home', nombre: 'Casa', icon: Home },
  { id: 'tag', nombre: 'Etiqueta', icon: Tag },
];

// Colores predefinidos
const COLORES_DISPONIBLES = [
  '#EF4444', // Rojo
  '#F59E0B', // Naranja
  '#10B981', // Verde
  '#3B82F6', // Azul
  '#8B5CF6', // Violeta
  '#EC4899', // Rosa
  '#6B7280', // Gris
  '#14B8A6', // Teal
];

export default function AdminEtiquetasRecursero() {
  const { token } = useAuth();
  const [etiquetas, setEtiquetas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Formulario
  const [showForm, setShowForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    color: '#3B82F6',
    icono: 'tag'
  });
  const [saving, setSaving] = useState(false);
  
  // Geocodificación masiva
  const [geocodificando, setGeocodificando] = useState(false);
  const [resultadoGeo, setResultadoGeo] = useState(null);
  const [statsGeo, setStatsGeo] = useState(null);

  useEffect(() => {
    cargarEtiquetas();
    cargarEstadisticasGeo();
  }, []);

  const cargarEstadisticasGeo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/recursero-geo/estadisticas`);
      setStatsGeo(response.data);
    } catch (err) {
      console.error('Error cargando stats geo:', err);
    }
  };

  const geocodificarMasivo = async () => {
    if (!window.confirm('Esto geocodificará todas las instituciones sin coordenadas. El proceso puede tardar varios minutos. ¿Continuar?')) {
      return;
    }
    
    setGeocodificando(true);
    setResultadoGeo(null);
    
    try {
      const response = await axios.post(`${API_URL}/api/recursero-geo/geocodificar-masivo`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResultadoGeo(response.data);
      cargarEstadisticasGeo();
    } catch (err) {
      setError('Error al geocodificar');
    } finally {
      setGeocodificando(false);
    }
  };

  const cargarEtiquetas = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/recursero-geo/etiquetas?solo_activas=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEtiquetas(response.data);
    } catch (err) {
      setError('Error al cargar etiquetas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    
    try {
      if (editando) {
        await axios.put(
          `${API_URL}/api/recursero-geo/etiquetas/${editando}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess('Etiqueta actualizada correctamente');
      } else {
        await axios.post(
          `${API_URL}/api/recursero-geo/etiquetas`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess('Etiqueta creada correctamente');
      }
      
      cargarEtiquetas();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (etiqueta) => {
    setEditando(etiqueta.id);
    setFormData({
      nombre: etiqueta.nombre,
      descripcion: etiqueta.descripcion,
      color: etiqueta.color,
      icono: etiqueta.icono
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Desactivar esta etiqueta? Las instituciones que la usen la conservarán.')) return;
    
    try {
      await axios.delete(`${API_URL}/api/recursero-geo/etiquetas/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Etiqueta desactivada');
      cargarEtiquetas();
    } catch (err) {
      setError('Error al desactivar');
    }
  };

  const handleReactivar = async (id) => {
    try {
      await axios.put(
        `${API_URL}/api/recursero-geo/etiquetas/${id}`,
        { activa: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Etiqueta reactivada');
      cargarEtiquetas();
    } catch (err) {
      setError('Error al reactivar');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditando(null);
    setFormData({
      nombre: '',
      descripcion: '',
      color: '#3B82F6',
      icono: 'tag'
    });
  };

  const getIconComponent = (iconId) => {
    const iconData = ICONOS_DISPONIBLES.find(i => i.id === iconId);
    return iconData ? iconData.icon : Tag;
  };

  return (
    <div className="space-y-6">
      {/* Estadísticas de Geolocalización */}
      {statsGeo && (
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              Geolocalización de Instituciones
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="text-center p-3 bg-white rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{statsGeo.total_instituciones}</p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
              <div className="text-center p-3 bg-white rounded-lg">
                <p className="text-2xl font-bold text-green-600">{statsGeo.con_coordenadas}</p>
                <p className="text-xs text-green-600">Con ubicación</p>
              </div>
              <div className="text-center p-3 bg-white rounded-lg">
                <p className="text-2xl font-bold text-orange-600">{statsGeo.sin_coordenadas}</p>
                <p className="text-xs text-orange-600">Sin ubicación</p>
              </div>
              <div className="text-center p-3 bg-white rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{statsGeo.porcentaje_geolocalizadas}%</p>
                <p className="text-xs text-blue-600">Cobertura</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Button 
                onClick={geocodificarMasivo} 
                disabled={geocodificando || statsGeo.sin_coordenadas === 0}
                variant="outline"
                className="gap-2"
              >
                {geocodificando ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Navigation className="w-4 h-4" />
                )}
                {geocodificando ? 'Geocodificando...' : `Geocodificar ${statsGeo.sin_coordenadas} pendientes`}
              </Button>
              
              {resultadoGeo && (
                <span className={`text-sm ${resultadoGeo.exitosas > 0 ? 'text-green-600' : 'text-orange-600'}`}>
                  ✓ {resultadoGeo.exitosas} de {resultadoGeo.total} geocodificadas
                </span>
              )}
            </div>
            
            <p className="text-xs text-gray-500 mt-2">
              La geocodificación automática usa las direcciones para obtener coordenadas. Puede tardar 1 segundo por institución.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Etiquetas del Recursero</h2>
          <p className="text-sm text-gray-500">
            Creá y gestioná las etiquetas/características para filtrar instituciones
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} disabled={showForm}>
          <Plus className="w-4 h-4 mr-2" />
          Nueva Etiqueta
        </Button>
      </div>

      {/* Mensajes */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          {success}
        </div>
      )}

      {/* Formulario */}
      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editando ? 'Editar Etiqueta' : 'Nueva Etiqueta'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre *</Label>
                  <Input
                    id="nombre"
                    value={formData.nombre}
                    onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                    placeholder="Ej: Guardia 24hs"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="descripcion">Descripción</Label>
                  <Input
                    id="descripcion"
                    value={formData.descripcion}
                    onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                    placeholder="Ej: Atención las 24 horas"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {/* Selector de color */}
                <div className="space-y-2">
                  <Label>Color</Label>
                  <div className="flex flex-wrap gap-2">
                    {COLORES_DISPONIBLES.map(color => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, color }))}
                        className={`w-8 h-8 rounded-full border-2 transition-all ${
                          formData.color === color ? 'border-gray-900 scale-110' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>

                {/* Selector de icono */}
                <div className="space-y-2">
                  <Label>Icono</Label>
                  <div className="flex flex-wrap gap-2">
                    {ICONOS_DISPONIBLES.map(({ id, nombre, icon: Icon }) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, icono: id }))}
                        className={`p-2 rounded border transition-all ${
                          formData.icono === id 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        title={nombre}
                      >
                        <Icon className="w-5 h-5" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <Label className="text-xs text-gray-500 mb-2 block">Vista previa</Label>
                <span 
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-sm font-medium"
                  style={{ backgroundColor: formData.color }}
                >
                  {React.createElement(getIconComponent(formData.icono), { className: "w-4 h-4" })}
                  {formData.nombre || 'Nombre de la etiqueta'}
                </span>
              </div>

              {/* Botones */}
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={resetForm}>
                  <X className="w-4 h-4 mr-2" />
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {editando ? 'Actualizar' : 'Crear'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Lista de etiquetas */}
      <Card>
        <CardHeader>
          <CardTitle>Etiquetas Existentes</CardTitle>
          <CardDescription>
            {etiquetas.filter(e => e.activa).length} activas, {etiquetas.filter(e => !e.activa).length} inactivas
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" />
            </div>
          ) : etiquetas.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Tag className="w-12 h-12 mx-auto text-gray-300 mb-2" />
              <p>No hay etiquetas creadas</p>
            </div>
          ) : (
            <div className="space-y-2">
              {etiquetas.map(etiqueta => {
                const IconComponent = getIconComponent(etiqueta.icono);
                return (
                  <div 
                    key={etiqueta.id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      etiqueta.activa ? 'bg-white' : 'bg-gray-50 opacity-60'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <GripVertical className="w-4 h-4 text-gray-300 cursor-move" />
                      <span 
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-white text-sm font-medium"
                        style={{ backgroundColor: etiqueta.color }}
                      >
                        <IconComponent className="w-4 h-4" />
                        {etiqueta.nombre}
                      </span>
                      {etiqueta.descripcion && (
                        <span className="text-sm text-gray-500">{etiqueta.descripcion}</span>
                      )}
                      {!etiqueta.activa && (
                        <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                          Inactiva
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {etiqueta.activa ? (
                        <>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleEdit(etiqueta)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleDelete(etiqueta.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      ) : (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleReactivar(etiqueta.id)}
                        >
                          Reactivar
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
